import { useCallback, useEffect, useMemo } from 'react'

import { CoreComponentFormProps, CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCompTableData } from '@/hooks/__project__/useCompTableData'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { formatColumnValue } from '@/shared/form-utils'
import { getSafely } from '@/shared/safely'
import { isEmpty } from '@/shared/utils'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { useUpdateFormDefaultValue } from '../../../hooks/useUpdateFormDefaultValue'
import { getColBrief } from '../../../utils'
import { CompSelectProps } from './comp'

export interface CompSelectWrapperCoreProps {
  infos: CoreComponentInfos
  form: CoreComponentFormProps
  tableData: CoreComponentTableData
  children: (props: CompSelectProps) => React.ReactNode
}

/**
 * CompSelectWrapperCore
 */
export const CompSelectWrapperCore = ({
  infos: { component, settings, containerClient, detailTableId, detailRowIndex },
  form,
  tableData: { dbTable: optionsTable },
  children,
}: CompSelectWrapperCoreProps) => {
  const { openComponentSettings, isDevelopmentMode } = useAppBuilderContext()

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // ========================= form settings =========================
  const { dataSourceTable, formState, setFormState, setRequiredFieldStatus } = form

  // ========================= data =========================
  const columnId = settings[ComponentSettingKey.SelectionDataValue] as string
  const column = dataSourceTable?.columnsMap?.get(columnId)

  const optionsSortType = settings[ComponentSettingKey.SelectionDataOptionsSortType] as string
  const optionsSortBy = settings[ComponentSettingKey.SelectionDataOptionsSortBy] as string
  const optionsFilterSetting = settings[ComponentSettingKey.SelectionDataOptionsFilter]

  // get options rows
  const [{ rows: optionsRows }] = useCompTableData({
    componentId: component._id,
    table: optionsTable,
    filterSetting: optionsFilterSetting,
    sortSetting: {
      sortType: optionsSortType,
      sortBy: optionsSortBy,
    },
  })

  const multiple = settings[ComponentSettingKey.SelectionDataMultiple]
  const selectionType = settings[ComponentSettingKey.SelectionDesignType] as string
  const helpTextData = settings[ComponentSettingKey.SelectionDataHelp]
  const helpText = useParseVariableText(helpTextData, containerClient, detailTableId, detailRowIndex)

  const valueColumnName = optionsTable?.columnsMap.get(columnId)?._Name
  const labelData = settings[ComponentSettingKey.SelectionDataLabel] ?? valueColumnName
  const label = useParseVariableText(labelData, containerClient, detailTableId, detailRowIndex)

  const optionValueColId = getColBrief(optionsTable, settings[ComponentSettingKey.SelectionDataOptionsValue] as string)
  const optionLabelColId = getColBrief(optionsTable, settings[ComponentSettingKey.SelectionDataOptionsLabel] as string)
  const optionIconColId = getColBrief(optionsTable, settings[ComponentSettingKey.SelectionDataOptionsIcon] as string)

  const options = useMemo(() => {
    if (!columnId) return []

    return (
      optionsRows
        .map((row) => {
          return {
            id: row._id,
            value: (optionValueColId ? row[optionValueColId._id]?.value : undefined) as string,
            label: optionLabelColId ? row[optionLabelColId._id]?.toString() : '',
            icon: optionIconColId ? row[optionIconColId._id]?.toString() : undefined,
          }
        })
        .filter((v) => v.value !== null && typeof v.value !== 'undefined')
        // filter by unique value
        .filter((v, idx, self) => self.findIndex((t) => t.value === v.value) === idx)
    )
    //
  }, [columnId, optionsRows, optionValueColId, optionLabelColId, optionIconColId])

  const filteredOptions = options

  const defaultValue = useParseVariableSelect<string>(
    settings[ComponentSettingKey.SelectionDataDefaultValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const isEditable = getSafely(settings, [ComponentSettingKey.SelectionDataDefaultValueEditable], true) as boolean

  // ========================= design =========================

  const leadingIcon = settings[ComponentSettingKey.SelectionDesignLeadingIcon] as string
  const padding = settings[ComponentSettingKey.SelectionDesignMargin]

  // ========================= behavior =========================

  const required = settings[ComponentSettingKey.SelectionBehaviorRequired]

  // ========================= derived =========================

  // const isMultiple = useMemo(() => {
  //   if (valueCol) {
  //     if ([ColumnTypes.Array, ColumnTypes.ImageArray].includes(valueCol._Type)) {
  //       return true
  //     }
  //     if (valueCol._Type === ColumnTypes.Text && multiple) {
  //       return true
  //     }
  //   }
  //   return false
  // }, [multiple, valueCol])

  // ========================= form state =========================

  const value = formState[columnId]

  const handleUpdateDefaultValue = useCallback(
    (defValue: string | number | undefined) => {
      if (defValue === undefined || defValue === null) return
      setFormState((prev) => ({ ...prev, [columnId]: formatColumnValue(defValue, column) }))
    },
    [columnId, column, setFormState]
  )

  useUpdateFormDefaultValue({
    defaultValue: formatColumnValue(defaultValue, column),
    value: formState[columnId],
    onUpdate: handleUpdateDefaultValue,
  })

  const handleChangeValue = (value: any) => {
    if (!columnId) return
    setFormState((prev) => ({ ...prev, [columnId]: formatColumnValue(value, column) }))
  }

  const handleClearValue = () => {
    if (!columnId) return
    setFormState((prev) => ({ ...prev, [columnId]: null }))
  }

  // Add effect to update required field status
  useEffect(() => {
    if (required) {
      setRequiredFieldStatus(columnId, isEmpty(value))
    }
    return () => {
      if (required) {
        setRequiredFieldStatus(columnId, false)
      }
    }
  }, [required, value, columnId, setRequiredFieldStatus])

  // ========================= render =========================
  const baseSelectProps: CompSelectProps = {
    type: selectionType as any,
    label,
    required,
    options: filteredOptions,
    value: typeof value === 'undefined' || value === null ? '' : value.toString(),
    onChange: handleChangeValue,
    onClear: handleClearValue,
    supportingText: helpText,
    disabled: !isEditable,
    // isMultiple: false, // multiple is disabled for now
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(baseSelectProps)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(baseSelectProps)
}
